import {
  pagination_queries_interface,
  pagination_response_interface,
} from "./Core/Pagination";

export interface TableBills {
  created_at: string;
  uuid: string;
  event: string;
  concession: string;
  seller: string;
  name: string;
  products_quantity: number;
  subtotal: number;
  status: number;
}

export enum TableBillsStatus {
  CANCELLED = 0,
  OPEN = 1,
  TMP = 2,
  CLOSED = 3,
  PAID = 4,
  PENDING_VALIDATION = 5,
}

export const TableBillStatus: { [key: string]: string } = {
  0: "Cancelada",
  1: "Abierta",
  2: "Pendiente",
  3: "Cerrada",
  4: "Pagada",
  5: "Por confirmar",
};

export interface GetTableBillsResponse extends pagination_response_interface {
  data: TableBills[];
}

export interface GetTableBillsQueries extends pagination_queries_interface {
  venue?: string;
  concession?: string;
  orderBy?: string;
  order?: string;
  status?: string;
  search_by?: string;
  search_value?: string;
  event: string;
  is_system?: string;
}

export interface BillSession {
  uuid: string;
  session_name: string | null;
  /**
   * YOS-10255: seller the detail names. `primary_session_name` is the terminal session the seller key was
   * validated from (the raw `session_name` when there is none), and `secondary_seller_name` the key holder.
   * Both are optional so an older API response still renders through the `session_name` fallback.
   */
  primary_session_name?: string | null;
  secondary_seller_name?: string | null;
  secondary_seller_code?: string | null;
  device: string;
}

export interface BillEvent {
  uuid: string;
  name: string;
  bin: string;
}

export interface ProductParent {
  uuid: string;
}

export interface BillProduct {
  uuid_parent: string | null;
  parent?: ProductParent | null;
  product: string;
  price: number;
  base_price: number;
  quantity: number;
  subtotal: number;
  name_concession: string;
  uuid: string;
  uuid_concession: string;
  uuid_product: string;
  uuid_sale: string;
  fee: number;
  comments: string[];
  /**
   * True when the product has at least one active inventory supply (insumo) linked.
   * When `false`, the restock step shows an informative message instead of the Sí/No toggle.
   */
  has_supplies?: boolean;
  /**
   * YOS-10255: status of the active kitchen order (Orders.status) holding this product:
   * 0 Pending, 1 Processing, 2 Ready, 3 Completed. Null when the product has no order.
   * Statuses 1-3 mean the product is already in preparation and cancelling needs confirmation.
   */
  order_status?: number | null;
}

/** YOS-10255: Orders.status values that mean the product already went to the kitchen. */
export const ORDER_STATUSES_IN_PREPARATION: number[] = [1, 2, 3];

export interface BillConcession {
  uuid: string;
  name: string;
}

export interface BillSeller {
  uuid: string;
  username: string;
  name: string;
}

export interface BillVenue {
  uuid: string;
  short_name: string;
}

export interface BillSalePayment {
  uuid: string;
  code: string;
  subtotal: number;
  tip: number;
  total: number;
  status: number;
  nfc_transaction?: NfcTransaction;
  card_transaction?: CardTransaction;
  qr_transaction?: QrTransaction;
  palm_transaction?: PalmTransaction;
  cash_transaction?: CashTransaction;
}

export interface BillSale {
  subtotal: number;
  total: number;
  products: BillProduct[];
  fee: number;
  grand_total: number;
  discount: number;
  concession: BillConcession;
  created_at: string;
  event: {
    uuid: string;
    name: string;
  };
  code: string;
  sold_at: string;
  seller: BillSeller;
  uuid_session: string;
  status: number;
  status_description: string;
  tip: number;
  uuid: string;
  comments?: string[];
  payments?: BillSalePayment[];
  uuid_concession: string;
  uuid_event: string;
  uuid_seller: string;
  uuid_venue: string;
  total_products: number;
  venue: BillVenue;
  uuid_bill: string;
}

export interface BillLogSaleProduct {
  uuid: string;
  uuid_sale: string;
  uuid_product: string;
  product: string;
}

export interface BillLogBill {
  uuid: string;
  name: string;
}

export interface BillLog {
  uuid: string;
  pre_quantity: number;
  post_quantity: number;
  created_at: string;
  uuid_bill: string;
  bill: BillLogBill;
  uuid_sale_product: string;
  sale_product: BillLogSaleProduct;
}

export interface GetBillDetailResponse {
  uuid: string;
  name: string;
  uuid_session: string;
  session: BillSession;
  uuid_event: string;
  event: BillEvent;
  status: number;
  status_description: string;
  created_at: string;
  subtotal: number;
  tips: number;
  app_service_fee: number;
  total: number;
  sales: BillSale[];
  logs: BillLog[];
  payments: Payment[];
}

interface Payment {
  uuid: string;
  code: string;
  subtotal: number;
  tip: number;
  total: number;
  paid_at: string;
  created_at: string;
  status: number;
  nfc_transaction?: NfcTransaction;
  card_transaction?: CardTransaction;
  qr_transaction?: QrTransaction;
  palm_transaction?: PalmTransaction;
  cash_transaction?: CashTransaction;
}

interface PalmTransaction {
  uuid: string;
  terminal: string;
  palm_fingerprint?: {
    uuid: string;
    customer?: {
      phone_number: string;
    };
  };
}

interface CardTransaction {
  uuid: string;
  transaction_id: string;
  authorization: string;
  pin_used: number;
  approved: number;
  has_refund: number;
  rejection_reason: string;
  operation_key: string;
  afiliation: string;
  sale_key: string;
  terminal: string;
  entry_mode: string;
  longitude: string;
  latitude: string;
  type: string;
  sale_code: string;
  is_reversed: number;
  is_cancelled: number;
  date: string;
  application_date: string;
  currency: string;
  card: Card;
  created_at: string;
  merchant?: string;
}

interface QrTransaction {
  tip: number;
  uuid: string;
  subtotal: number;
  total: number;
  type: string;
  terminal: string;
  qr_wallet: {
    uuid: string;
    name: string;
    customer: {
      name: string;
      last_name: string;
      uuid: string;
    };
  };
  venue_wallet: {
    uuid: string;
    venue: {
      uuid: string;
      name: string;
    };
  };
}

interface CashTransaction {
  uuid: string;
}

interface Card {
  uuid: string;
  bin: string;
  last_four: string;
  brand: string;
  bank: string;
  type: string;
}

interface NfcTransaction {
  transaction_id: string;
  uuid: string;
  uuid_sale: string;
  id_sale: number;
  previous_balance: number;
  post_balance: number;
  nfc_card: NfcCard;
}

interface NfcCard {
  uuid: string;
  serial_number: string;
  bin: string;
}

export interface PartialCancelBillBody {
  products: { uuid_sale_product: string; quantity: number; restock?: boolean }[];
  phone_number?: string;
  payments?: { uuid: string; refund_amount: number }[];
  comment: string;
}

/**
 * Decisión de reinventario por producto enviada al endpoint de cancelación total de cuenta.
 * El backend la respeta únicamente cuando la request proviene de Dashboard.
 */
export interface CancelBillProductRestock {
  uuid_sale_product: string;
  restock: boolean;
}
